import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { UpgradeProvider } from "@/context/UpgradeContext";
import CookieConsent from "@/components/CookieConsent";
import AuthCallback from "@/pages/AuthCallback";
import Login from "@/pages/Login";
import PatchNotes from "@/pages/PatchNotes";
import StoreCopy from "@/pages/StoreCopy";
import ColdEmail from "@/pages/ColdEmail";
import Pricing from "@/pages/Pricing";
import Dashboard from "@/pages/Dashboard";
import Settings from "@/pages/Settings";
import History from "@/pages/History";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";

const Loading = () => (
  <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
    <div className="h-10 w-10 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

function AppRouter() {
  const location = useLocation();
  // Process OAuth callback BEFORE anything else (read hash from useLocation, not window.location)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/patch-notes" element={<ProtectedRoute><PatchNotes /></ProtectedRoute>} />
      <Route path="/store-copy" element={<ProtectedRoute><StoreCopy /></ProtectedRoute>} />
      <Route path="/cold-email" element={<ProtectedRoute><ColdEmail /></ProtectedRoute>} />
      <Route path="/pricing" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />
      <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const ThemedToaster = () => {
  const { theme } = useTheme();
  return <Toaster position="bottom-right" theme={theme} />;
};

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <UpgradeProvider>
            <AppRouter />
            <CookieConsent />
            <ThemedToaster />
          </UpgradeProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
